import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import { TEST_ID, WGS84_CHECK_MARK } from './constants';
import type { ApiPrescriptionZone, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const formatRate = (value: number): string => value.toLocaleString(undefined);

const computeRateRangeText = (
    zones: ApiPrescriptionZone[],
    unit: string,
    singleFn: (rate: string, unit: string) => string,
    spanFn: (min: string, max: string, unit: string) => string
): string => {
    if (zones.length === 0) {
        return singleFn('0', unit);
    }
    const rates = zones.map(zone => zone.rate);
    const min = Math.min(...rates);
    const max = Math.max(...rates);

    if (min === max) {
        return singleFn(formatRate(min), unit);
    }

    return spanFn(formatRate(min), formatRate(max), unit);
};

const ShapefilePreview: FunctionComponent<Props> = ({
    approxAreaLabel,
    areaValueText,
    className = '',
    crsLabel,
    crsValueFormatter,
    fileLabel,
    fileName,
    rateColumn,
    rateColumnLabel,
    rateRangeLabel,
    rateSingleFormatter,
    rateSpanFormatter,
    rateUnit,
    testId = TEST_ID,
    zoneCountLabel,
    zones
}) => {
    const rateRangeText = computeRateRangeText(
        zones,
        rateUnit,
        rateSingleFormatter,
        rateSpanFormatter
    );
    const crsText = crsValueFormatter(WGS84_CHECK_MARK);
    const zoneCount = zones.length.toString();

    return (
        <VerticalContainer className={`${className} ${styles.shapefilePreview}`} testId={testId}>
            <HorizontalContainer className={styles.previewRow}>
                <Wrapper className={styles.previewLabel}>{fileLabel}</Wrapper>

                <Wrapper className={styles.previewValue}>{fileName}</Wrapper>
            </HorizontalContainer>

            <HorizontalContainer className={styles.previewRow}>
                <Wrapper className={styles.previewLabel}>{crsLabel}</Wrapper>

                <Wrapper className={styles.previewValue}>{crsText}</Wrapper>
            </HorizontalContainer>

            <HorizontalContainer className={styles.previewRow}>
                <Wrapper className={styles.previewLabel}>{zoneCountLabel}</Wrapper>

                <Wrapper className={styles.previewValue}>{zoneCount}</Wrapper>
            </HorizontalContainer>

            <HorizontalContainer className={styles.previewRow}>
                <Wrapper className={styles.previewLabel}>{rateColumnLabel}</Wrapper>

                <Wrapper className={styles.previewValue}>{rateColumn}</Wrapper>
            </HorizontalContainer>

            <HorizontalContainer className={styles.previewRow}>
                <Wrapper className={styles.previewLabel}>{rateRangeLabel}</Wrapper>

                <Wrapper className={styles.zoneTag}>{rateRangeText}</Wrapper>
            </HorizontalContainer>

            <Wrapper className={styles.previewDivider} />

            <HorizontalContainer className={styles.previewRow}>
                <Wrapper className={styles.previewLabel}>{approxAreaLabel}</Wrapper>

                <Wrapper className={styles.previewValue}>{areaValueText}</Wrapper>
            </HorizontalContainer>
        </VerticalContainer>
    );
};

export default ShapefilePreview;
